import type { Chapter, ChapterPlan, InspectorBeatRepairRequest, ValidationResult } from '../types';
import type { FourBeatChapterSession } from './fourBeatInspectorOrchestration.service';
import { shouldAttemptInspectorBeatRepair } from './fourBeatInspectorOrchestration.service';
import { FOUR_BEAT_AUTHORITATIVE_IDS } from './scribeThinkingPolicy.service';

export type InspectorDecisionRoute =
  | 'ACCEPT'
  | 'TARGETED_REPAIR'
  | 'TERMINAL_CANON_BLOCK'
  | 'RETRY_INSPECTOR'
  | 'RECOVERABLE_SCHEMA_FAILURE'
  | 'RECOVERABLE_INSPECTOR_FAILURE';

export interface InspectorResponseShapeDiagnostic {
  responseKeys: string[];
  verdictFieldPresent: boolean;
  issueCount: number;
  issueTypes: string[];
  repairableIssueCount: number;
  blockingIssueCount: number;
  roadmapIssueCount: number;
  canonIssueCount: number;
  parseAccepted: boolean;
  rejectionCode?: string;
}

export interface InspectorDecisionDiagnostic {
  chapterNumber: number;
  verdict: string;
  route: InspectorDecisionRoute;
  reasonCodes: string[];
  inspectorCallsUsed: number;
  repairCallsUsed: number;
}

export const REPAIRABLE_INSPECTOR_REASON_CODES = new Set([
  'SUMMARY_COMPRESSION',
  'POSSIBLE_SUMMARY_COMPRESSION',
  'ROADMAP_REQUIREMENT_MISSING',
  'MISSING_ROADMAP_BEAT',
  'UNDERDEVELOPED_TRANSITION',
  'INSUFFICIENT_DRAMATIZATION',
  'REPEATED_REVELATION',
  'UNRESOLVED_PHYSICAL_CONTINUITY',
  'MISSING_OBJECT_CARRYOVER',
  'MISSING_ATOMIC_EVENT',
  'BEAT_UNDERDEVELOPED',
]);

export const CANON_TERMINAL_REASON_CODES = new Set([
  'CONFIRMED_CANON_CONTRADICTION',
  'CHARACTER_RESURRECTION',
  'DESTROYED_OBJECT_REVERSAL',
  'IDENTITY_CONTAMINATION',
  'CROSS_PROJECT_CONTAMINATION',
  'INVALID_CHRONOLOGY',
  'PROHIBITED_RESURRECTION',
]);

const ROADMAP_REASON_FRAGMENT = /roadmap|required beat|atomic event|event id/i;
const SUMMARY_REASON_FRAGMENT = /summary compression|compressed|underdeveloped|transition|dramati/i;

export function analyzeInspectorResponseShape(
  raw: Record<string, unknown> | null,
  parseAccepted: boolean,
  rejectionCode?: string,
): InspectorResponseShapeDiagnostic {
  const responseKeys = raw ? Object.keys(raw) : [];
  const verdictFieldPresent = Boolean(raw?.verdict || raw?.status);
  const beatRepairRequests = Array.isArray(raw?.beatRepairRequests) ? raw!.beatRepairRequests : [];
  const issueTypes = beatRepairRequests.flatMap((item: any) =>
    Array.isArray(item?.reasonCodes) ? item.reasonCodes.map(String) : [],
  );
  const repairableIssueCount = issueTypes.filter((code) => REPAIRABLE_INSPECTOR_REASON_CODES.has(code)).length;
  const blockingIssueCount = issueTypes.filter((code) => CANON_TERMINAL_REASON_CODES.has(code)).length;
  const roadmapIssueCount = issueTypes.filter((code) => String(code).includes('ROADMAP')).length
    + (SUMMARY_REASON_FRAGMENT.test(String(raw?.reason || '')) ? 0 : 0);
  const canonIssueCount = blockingIssueCount;

  return {
    responseKeys,
    verdictFieldPresent,
    issueCount: issueTypes.length,
    issueTypes,
    repairableIssueCount,
    blockingIssueCount,
    roadmapIssueCount,
    canonIssueCount,
    parseAccepted,
    rejectionCode,
  };
}

export function logInspectorResponseShape(shape: InspectorResponseShapeDiagnostic): void {
  console.log(`[INSPECTOR RESPONSE SHAPE] ${JSON.stringify(shape)}`);
}

export function logInspectorDecision(decision: InspectorDecisionDiagnostic): void {
  console.log(`[INSPECTOR DECISION] ${JSON.stringify(decision)}`);
}

export function syncInspectorCallCount(
  session: FourBeatChapterSession | null,
  inspectorCallsUsed: number,
): void {
  if (!session) return;
  session.inspectorCallsUsed = inspectorCallsUsed;
}

export function recordInspectorLogicalCall(
  session: FourBeatChapterSession | null,
  currentCount: number,
): number {
  const next = currentCount + 1;
  syncInspectorCallCount(session, next);
  return next;
}

export function extractReasonCodes(validation: ValidationResult): string[] {
  const codes = new Set<string>();
  for (const request of validation.beatRepairRequests || []) {
    for (const code of request.reasonCodes || []) {
      if (code) codes.add(code);
    }
  }
  const reason = String(validation.reason || '');
  for (const code of REPAIRABLE_INSPECTOR_REASON_CODES) {
    if (reason.toUpperCase().includes(code)) codes.add(code);
  }
  for (const code of CANON_TERMINAL_REASON_CODES) {
    if (reason.toUpperCase().includes(code)) codes.add(code);
  }
  return [...codes];
}

export function isCanonTerminalValidation(validation: ValidationResult): boolean {
  if (validation.verdict !== 'BLOCKED') return false;
  const codes = extractReasonCodes(validation);
  if (codes.some((code) => CANON_TERMINAL_REASON_CODES.has(code))) return true;
  const reason = String(validation.reason || '').toLowerCase();
  return (
    reason.includes('canon contradiction') ||
    reason.includes('resurrection') ||
    reason.includes('destroyed object') ||
    reason.includes('contamination') ||
    reason.includes('cross-project')
  );
}

export function isRepairableValidation(validation: ValidationResult): boolean {
  if (shouldAttemptInspectorBeatRepair(validation)) return true;
  const codes = extractReasonCodes(validation);
  if (codes.some((code) => REPAIRABLE_INSPECTOR_REASON_CODES.has(code))) return true;
  const reason = String(validation.reason || '');
  return ROADMAP_REASON_FRAGMENT.test(reason) || SUMMARY_REASON_FRAGMENT.test(reason);
}

export function inferBeatRepairRequest(
  validation: ValidationResult,
  chapter?: Chapter | null,
  plan?: ChapterPlan | null,
): InspectorBeatRepairRequest | null {
  const reason = String(validation.reason || validation.correction_order || '').trim();
  if (!reason) return null;
  let beatId = validation.beatRepairRequests?.[0]?.beatId;
  if (!beatId) {
    const match = reason.match(/beat-([1-4])/i);
    beatId = match ? (`beat-${match[1]}` as InspectorBeatRepairRequest['beatId']) : 'beat-1';
  }
  if (!FOUR_BEAT_AUTHORITATIVE_IDS.includes(beatId as any)) {
    beatId = 'beat-1';
  }
  const reasonCodes = extractReasonCodes(validation);
  const normalizedCodes = reasonCodes.length > 0
    ? reasonCodes
    : ROADMAP_REASON_FRAGMENT.test(reason)
      ? ['ROADMAP_REQUIREMENT_MISSING']
      : ['SUMMARY_COMPRESSION'];
  return {
    beatId: beatId as InspectorBeatRepairRequest['beatId'],
    reasonCodes: normalizedCodes,
    explanation: reason.slice(0, 500),
  };
}

export function promoteRepairableInspectorVerdict(
  validation: ValidationResult,
  chapter?: Chapter | null,
  plan?: ChapterPlan | null,
): ValidationResult {
  if (validation.verdict === 'PASS' || validation.status === 'PASS') return validation;
  if (shouldAttemptInspectorBeatRepair(validation)) return validation;
  if (isCanonTerminalValidation(validation)) return validation;
  if (!isRepairableValidation(validation)) return validation;

  const inferred = inferBeatRepairRequest(validation, chapter, plan);
  if (!inferred) return validation;

  return {
    ...validation,
    status: 'FAIL',
    verdict: 'REPAIR_REQUIRED',
    beatRepairRequests: [inferred],
  };
}

export function classifyInspectorDecision(params: {
  validation: ValidationResult;
  inspectorCallsUsed: number;
  repairCallsUsed?: number;
  infrastructureFailure?: boolean;
  infrastructureCode?: string;
}): InspectorDecisionDiagnostic {
  const repairCallsUsed = params.repairCallsUsed ?? 0;
  const verdict = String(params.validation.verdict || params.validation.status || 'UNKNOWN');
  const reasonCodes = extractReasonCodes(params.validation);

  if (params.infrastructureFailure) {
    return {
      chapterNumber: 0,
      verdict,
      route: 'RECOVERABLE_INSPECTOR_FAILURE',
      reasonCodes: params.infrastructureCode ? [params.infrastructureCode] : ['INSPECTOR_INFRASTRUCTURE_FAILURE'],
      inspectorCallsUsed: params.inspectorCallsUsed,
      repairCallsUsed,
    };
  }

  if (params.validation.verdict === 'PASS' || params.validation.status === 'PASS') {
    return {
      chapterNumber: 0,
      verdict,
      route: 'ACCEPT',
      reasonCodes,
      inspectorCallsUsed: params.inspectorCallsUsed,
      repairCallsUsed,
    };
  }

  if (shouldAttemptInspectorBeatRepair(params.validation)) {
    return {
      chapterNumber: 0,
      verdict,
      route: 'TARGETED_REPAIR',
      reasonCodes,
      inspectorCallsUsed: params.inspectorCallsUsed,
      repairCallsUsed,
    };
  }

  if (isCanonTerminalValidation(params.validation)) {
    return {
      chapterNumber: 0,
      verdict,
      route: 'TERMINAL_CANON_BLOCK',
      reasonCodes,
      inspectorCallsUsed: params.inspectorCallsUsed,
      repairCallsUsed,
    };
  }

  if (isRepairableValidation(params.validation)) {
    return {
      chapterNumber: 0,
      verdict,
      route: 'TARGETED_REPAIR',
      reasonCodes,
      inspectorCallsUsed: params.inspectorCallsUsed,
      repairCallsUsed,
    };
  }

  if (params.validation.verdict === 'BLOCKED') {
    return {
      chapterNumber: 0,
      verdict,
      route: 'TERMINAL_CANON_BLOCK',
      reasonCodes,
      inspectorCallsUsed: params.inspectorCallsUsed,
      repairCallsUsed,
    };
  }

  return {
    chapterNumber: 0,
    verdict,
    route: 'TERMINAL_CANON_BLOCK',
    reasonCodes,
    inspectorCallsUsed: params.inspectorCallsUsed,
    repairCallsUsed,
  };
}

export function buildInspectorFailureUserMessage(route: InspectorDecisionRoute, reasonCodes: string[]): string {
  if (route === 'RECOVERABLE_INSPECTOR_FAILURE' || route === 'RECOVERABLE_SCHEMA_FAILURE') {
    return 'The chapter was written, but Chronovela could not complete its validation check. The draft was preserved. Retry validation without rewriting the chapter.';
  }
  if (route === 'TERMINAL_CANON_BLOCK') {
    const category = reasonCodes.find((code) => CANON_TERMINAL_REASON_CODES.has(code)) || 'CONTINUITY_VALIDATION';
    return `The chapter could not be validated safely (${category.replace(/_/g, ' ').toLowerCase()}). The draft was preserved for review. It was not saved.`;
  }
  if (route === 'TARGETED_REPAIR') {
    return 'The chapter needs a targeted beat repair before it can continue. The draft was preserved.';
  }
  return 'The chapter could not be validated safely after targeted beat review. The draft was preserved. It was not saved.';
}

export function isInfrastructureInspectorError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = String((error as Error).message || '').toLowerCase();
  const name = String((error as Error).name || '');
  return (
    name === 'InspectorResponseParseError' ||
    name === 'InspectorInfrastructureError' ||
    message.includes('failed to parse ai response') ||
    message.includes('empty response from ai') ||
    message.includes('timed out') ||
    message.includes('failed to fetch') ||
    message.includes('503') ||
    message.includes('502') ||
    message.includes('504') ||
    message.includes('network')
  );
}
